import {
  Box,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import { SharedTooltip } from '../../HomePage';
import { GenericMostStat } from '../../../../api/interfaces/Dashboard';
import { Program } from '../../../../api/interfaces/Program';
import { BarChartSkeleton } from '../../../../components/skeleton/BarChartSkeleton/BarChartSkeleton';
import { NO_DATA_TO_DISPLAY_URL } from '../../../../utils/constants';

type Props = {
  isDashboardLoading: boolean;
  isMostWatchedVideosFilterLoading: boolean;
  selectedVideosFilterProgram: string;
  handleVideosFilterByProgramChange: (e: any) => void;
  programs: Program[];
  overviewMostWatchedVideos: GenericMostStat[];
  getBarChartColor: (index: number) => string;
};

export default function MostWatchedVideosCard({
  isDashboardLoading,
  isMostWatchedVideosFilterLoading,
  selectedVideosFilterProgram,
  handleVideosFilterByProgramChange,
  programs,
  overviewMostWatchedVideos,
  getBarChartColor,
}: Props) {
  return (
    <Card sx={{ height: 400 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Most Watched Videos
          </Typography>

          {isDashboardLoading ? (
            <Skeleton variant="text" width="30%" height={50} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 400, color: '#818181' }}>
                view by
              </Typography>
              <FormControl size="small">
                <Select
                  value={selectedVideosFilterProgram}
                  onChange={handleVideosFilterByProgramChange}
                  defaultValue="all programs"
                  disabled={isMostWatchedVideosFilterLoading}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Select Program' }}
                  sx={{
                    minWidth: 150,
                    fontSize: '12px',
                    color: '#818181',
                    '.MuiSelect-icon': {
                      color: '#818181',
                    },
                  }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="">all programs</MenuItem>
                  {programs.map((program) => (
                    <MenuItem key={program._id} value={program._id}>
                      <Typography
                        noWrap
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                        }}
                      >
                        {program.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          {(isDashboardLoading || isMostWatchedVideosFilterLoading) ? (
            <BarChartSkeleton />
          ) : overviewMostWatchedVideos.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#818181'
              }}
            >
              <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={overviewMostWatchedVideos}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="title"
                  interval={0}
                  tick={({ x, y, payload }) => {
                    const value = payload?.value || '';
                    const maxLineLength = 15;
                    const maxLines = 2;

                    const words = value.split(' ');
                    const lines: string[] = [];

                    let currentLine = '';
                    for (const word of words) {
                      if ((currentLine + word).length <= maxLineLength) {
                        currentLine += `${word} `;
                      } else {
                        lines.push(currentLine.trim());
                        currentLine = `${word} `;
                      }

                      if (lines.length === maxLines - 1 && currentLine.length > maxLineLength) {
                        currentLine = `${currentLine.slice(0, maxLineLength - 1)}…`;
                        break;
                      }
                    }
                    if (lines.length < maxLines) {
                      lines.push(currentLine.trim());
                    }

                    return (
                      <g transform={`translate(${x},${y + 10})`}>
                        {lines.slice(0, maxLines).map((line, index) => (
                          <text
                            key={index}
                            x={0}
                            y={index * 10}
                            textAnchor="middle"
                            fontSize="10px"
                            fill="#666"
                          >
                            {line.length > maxLineLength ? `${line.slice(0, maxLineLength)}…` : line}
                          </text>
                        ))}
                      </g>
                    );
                  }}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                />
                <Tooltip content={<SharedTooltip />} />
                <Bar dataKey="views">
                  {overviewMostWatchedVideos.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={getBarChartColor(index)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>


      </CardContent>
    </Card>
  );
}
